import { motion } from "framer-motion";
import { Check } from "lucide-react";
import confetti from "canvas-confetti";
import { useEffect, useState } from "react";
import { getCategoryIcon } from "@/lib/categoryIcons";
import { CURRENCY_SYMBOLS } from "@/lib/currencyConversion";
interface SuccessCardProps {
  amount: number;
  currency: string;
  category: string;
  merchant: string;
  note?: string;
  streakDays?: number;
  calmMode?: boolean;
  motivationStyle?: 'minimal' | 'gentle' | 'gamified';
  onComplete: () => void;
}

export const SuccessCard = ({
  amount,
  currency,
  category,
  merchant,
  note,
  streakDays = 0,
  calmMode = false,
  motivationStyle = 'gentle',
  onComplete,
}: SuccessCardProps) => {
  const [displayAmount, setDisplayAmount] = useState(0);

  useEffect(() => {
    // Trigger confetti if not calm mode
    if (!calmMode) {
      confetti({
        particleCount: 50,
        spread: 60,
        origin: { y: 0.6 },
        colors: ['#10b981', '#34d399', '#6ee7b7'],
      });
    }

    // Count up animation for amount
    const duration = 800;
    const steps = 30;
    const increment = amount / steps;
    const stepDuration = duration / steps;

    let current = 0;
    const timer = setInterval(() => {
      current += increment;
      if (current >= amount) {
        setDisplayAmount(amount);
        clearInterval(timer);
      } else {
        setDisplayAmount(current);
      }
    }, stepDuration);

    // Auto-complete after 3s (user feedback: previous 1.5s was too fast to read)
    const completeTimer = setTimeout(() => {
      onComplete();
    }, 3000);

    return () => {
      clearInterval(timer);
      clearTimeout(completeTimer);
    };
  }, [amount, calmMode, onComplete]);

  const icon = getCategoryIcon(category);
  const currencySymbol = CURRENCY_SYMBOLS[currency as keyof typeof CURRENCY_SYMBOLS] || currency;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      className="flex flex-col items-center justify-center py-8 space-y-4"
    >
      {/* Checkmark with bounce */}
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{
          type: "spring",
          stiffness: 260,
          damping: 20,
        }}
        className="w-16 h-16 rounded-full bg-green-500 flex items-center justify-center"
      >
        <Check className="w-8 h-8 text-white" />
      </motion.div>

      {/* Amount */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="text-4xl font-bold text-green-600 dark:text-green-400"
      >
        {currencySymbol}{displayAmount.toFixed(2)}
      </motion.div>

      {/* Category & Merchant */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="flex items-center gap-2 text-lg"
      >
        <span className="text-2xl">{icon}</span>
        <span className="font-medium">{category}</span>
        {merchant && merchant !== category && (
          <>
            <span className="text-muted-foreground">·</span>
            <span className="text-muted-foreground">{merchant}</span>
          </>
        )}
      </motion.div>

      {/* Note */}
      {note && (
        <motion.p
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="text-sm text-muted-foreground max-w-xs text-center"
        >
          {note}
        </motion.p>
      )}

      {/* Success message */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="text-lg font-medium text-foreground"
      >
        Looks good!
      </motion.div>

      {/* Streak celebration - only show in gentle/gamified */}
      {streakDays && streakDays > 0 && motivationStyle !== 'minimal' && (
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.5, type: "spring" }}
          className="flex items-center gap-2 px-4 py-2 rounded-full bg-orange-500/10 text-orange-600 dark:text-orange-400"
        >
          {motivationStyle === 'gamified' && <span className="text-xl">🔥</span>}
          <span className="font-semibold">{streakDays} day{streakDays !== 1 ? 's' : ''} in a row!</span>
        </motion.div>
      )}
    </motion.div>
  );
};
